import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Zap, Loader2, ArrowLeft, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:bg-white focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 outline-none transition-all";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) return setError("Password must be at least 6 characters");
    if (password !== confirm) return setError("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password reset! Please log in.");
      setTimeout(() => nav("/login"), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#FDF8FA] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="h-10 w-10 rounded-xl brand-gradient flex items-center justify-center shadow-lg shadow-pink-500/30"><Zap className="h-6 w-6 text-white" fill="white" /></div>
          <span className="font-display font-extrabold text-2xl">MrktMe</span>
        </Link>

        <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.06)] p-8 animate-fade-up">
          {done ? (
            <div data-testid="reset-success" className="text-center">
              <div className="h-14 w-14 rounded-2xl bg-green-100 flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="h-7 w-7 text-green-600" /></div>
              <h1 className="font-display text-2xl font-extrabold text-slate-900">Password reset!</h1>
              <p className="text-slate-500 text-sm mt-1">Redirecting you to log in…</p>
            </div>
          ) : !token ? (
            <div data-testid="reset-no-token" className="text-center">
              <h1 className="font-display text-2xl font-extrabold text-slate-900">Invalid link</h1>
              <p className="text-slate-500 text-sm mt-1">This reset link is missing its token. Please request a new one.</p>
              <Link to="/forgot-password" className="mt-5 inline-block font-bold text-pink-600 hover:underline">Request a new link</Link>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-6 w-6 text-fuchsia-600" />
                <h1 className="font-display text-2xl font-extrabold text-slate-900">Set a new password</h1>
              </div>
              <p className="text-slate-500 text-sm mt-1">Choose a strong password for your account.</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <input data-testid="reset-password" required type="password" placeholder="New password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
                <input data-testid="reset-confirm" required type="password" placeholder="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} />
                {error && <p data-testid="reset-error" className="text-sm text-rose-600 font-medium">{error}</p>}
                <button data-testid="reset-submit" disabled={loading} type="submit"
                  className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full font-bold text-white brand-gradient shadow-lg shadow-pink-500/30 hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60">
                  {loading && <Loader2 className="h-5 w-5 animate-spin" />} Reset password
                </button>
              </form>
            </>
          )}

          <Link to="/login" data-testid="reset-back" className="mt-6 flex items-center justify-center gap-1.5 text-sm text-slate-500 hover:text-pink-600 font-semibold">
            <ArrowLeft className="h-4 w-4" /> Back to log in
          </Link>
        </div>
      </div>
    </div>
  );
}
